import { z } from 'zod';

/**
 * Fiche récapitulative de contact (demande du 2026-09-22) : la feuille qu'on
 * imprime pour aller voir un candidat, ou qu'on pose sur la table du gérant.
 *
 * Six renseignements saisis par le recruteur, portés par la **candidature**
 * comme l'étiquette et le mémo : le même candidat peut viser deux postes, et
 * ce qu'on retient de lui n'est pas le même d'une annonce à l'autre.
 *
 * L'ÂGE y figure depuis le 2026-09-22 après-midi, sur demande explicite de
 * l'utilisateur (le matin même, il avait choisi la ligne à remplir au stylo ;
 * la feuille en main, il a demandé à le saisir). Ses limites sont fermes et
 * valent règle :
 *   * SAISI PAR LE RECRUTEUR, jamais demandé au candidat — le formulaire
 *     public le refuse toujours (`CHAMPS_INTERDITS`, tenue par un test) ;
 *   * JAMAIS UN CRITÈRE : ni filtre, ni motif d'écart, ni colonne d'export,
 *     ni question de barème. Il est écrit sur une feuille, rien de plus ;
 *   * texte libre (« 32 ans ») et non date de naissance : une date serait
 *     plus identifiante, et un nombre se périme tout seul.
 *
 * Restent proscrits partout : sexe, situation familiale, santé — l'interface
 * le rappelle sous les champs libres.
 */
export const AGE_MAX = 20;
export const RESIDENCE_MAX = 120;
export const NIVEAU_ETUDES_MAX = 120;
export const SPECIALITE_MAX = 120;
export const EXPERIENCES_MAX = 4000;
export const SALAIRE_ACTUEL_MAX = 60;
export const DISPONIBILITE_MAX = 120;
export const APPRECIATION_MAX = 4000;

/** Un texte vide efface le champ, comme `null` — même règle que le mémo. */
const champ = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((valeur) => (valeur ? valeur : null));

export const ficheRecapSchema = z
  .object({
    age: champ(AGE_MAX),
    residence: champ(RESIDENCE_MAX),
    niveauEtudes: champ(NIVEAU_ETUDES_MAX),
    specialite: champ(SPECIALITE_MAX),
    experiences: champ(EXPERIENCES_MAX),
    salaireActuel: champ(SALAIRE_ACTUEL_MAX),
    disponibilite: champ(DISPONIBILITE_MAX),
    appreciation: champ(APPRECIATION_MAX),
  })
  .strict();
export type FicheRecap = z.infer<typeof ficheRecapSchema>;

/** Les rubriques de la feuille, dans l'ordre où elle se lit. */
export const GROUPES_FICHE = {
  identite: 'Identité',
  parcours: 'Parcours',
  conditions: 'Conditions',
  appreciation: 'Appréciation du recruteur',
} as const;
export type GroupeFiche = keyof typeof GROUPES_FICHE;

/** Les huit champs, dans l'ordre où ils se lisent sur le papier. */
export const CHAMPS_FICHE = [
  { cle: 'age', groupe: 'identite', libelle: 'Âge', max: AGE_MAX, lignes: 1 },
  { cle: 'residence', groupe: 'identite', libelle: 'Lieu de résidence', max: RESIDENCE_MAX, lignes: 1 },
  { cle: 'niveauEtudes', groupe: 'parcours', libelle: 'Niveau d’études / formation', max: NIVEAU_ETUDES_MAX, lignes: 1 },
  { cle: 'specialite', groupe: 'parcours', libelle: 'Spécialité', max: SPECIALITE_MAX, lignes: 1 },
  { cle: 'experiences', groupe: 'parcours', libelle: 'Expériences professionnelles', max: EXPERIENCES_MAX, lignes: 5 },
  { cle: 'salaireActuel', groupe: 'conditions', libelle: 'Niveau de salaire actuel', max: SALAIRE_ACTUEL_MAX, lignes: 1 },
  { cle: 'disponibilite', groupe: 'conditions', libelle: 'Disponibilité', max: DISPONIBILITE_MAX, lignes: 1 },
  { cle: 'appreciation', groupe: 'appreciation', libelle: 'Appréciation du recruteur', max: APPRECIATION_MAX, lignes: 5 },
] as const satisfies ReadonlyArray<{
  cle: keyof FicheRecap;
  groupe: GroupeFiche;
  libelle: string;
  max: number;
  lignes: number;
}>;

/** Les champs d'une rubrique, pour l'écran comme pour le papier. */
export function champsDuGroupe(groupe: GroupeFiche): typeof CHAMPS_FICHE {
  return CHAMPS_FICHE.filter((champ) => champ.groupe === groupe) as unknown as typeof CHAMPS_FICHE;
}

/** Type MIME du document produit, servi en téléchargement. */
export const TYPE_PDF = 'application/pdf';
